import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Category } from '../../types';
import '../../styles/RecordFilters.css';
import '../../styles/MonthlySummary.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEraser, faCheckSquare } from '@fortawesome/free-solid-svg-icons';

interface MonthlySummaryFiltersProps {
  filters: {
    startDate: Date | null;
    endDate: Date | null;
    recordType: string;
    accountType: string;
    categoryId: number[]; // Array for multiple selection
    sortBy: string;
    sortOrder: string;
    amountCurrency: 'vnd' | 'usd';
  };
  categories: Category[];
  onFilterChange: (filters: any) => void;
  onSearch: () => void;
  onResetFilters: () => void;
}

const MonthlySummaryFilters: React.FC<MonthlySummaryFiltersProps> = ({ 
  filters, 
  categories, 
  onFilterChange, 
  onSearch, 
  onResetFilters 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');

  // Set current month display based on existing filters
  useEffect(() => {
    if (filters.startDate) {
      setCurrentMonth(filters.startDate);
    }
  }, [filters.startDate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.multi-select-container')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const formatMonthButton = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Handle month navigation - set to first day of month
  const handlePrevMonth = () => {
    const newStartDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const newEndDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0);
    setCurrentMonth(newStartDate);
    onFilterChange({
      ...filters,
      startDate: newStartDate,
      endDate: newEndDate
    });
  };

  const handleNextMonth = () => {
    const newStartDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    const newEndDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0);
    setCurrentMonth(newStartDate);
    onFilterChange({
      ...filters,
      startDate: newStartDate,
      endDate: newEndDate
    });
  };

  // Handle date changes - ensure we're working with full months
  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      // Set to first day of selected month
      const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      onFilterChange({
        ...filters,
        startDate: startOfMonth
      });
    } else {
      onFilterChange({
        ...filters,
        startDate: date
      });
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      // Set to last day of selected month
      const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      onFilterChange({
        ...filters,
        endDate: endOfMonth
      });
    } else {
      onFilterChange({
        ...filters,
        endDate: date
      });
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleCategoryChange = (categoryId: number) => {
    const currentIds = filters.categoryId || [];
    const newIds = currentIds.includes(categoryId)
      ? currentIds.filter(id => id !== categoryId)
      : [...currentIds, categoryId];
    
    onFilterChange({
      ...filters,
      categoryId: newIds
    });
  };

  const clearAllCategories = () => {
    onFilterChange({
      ...filters,
      categoryId: []
    });
  };

  const getSelectedCategoriesText = () => {
    const selectedIds = filters.categoryId || [];
    if (selectedIds.length === 0) return 'All Categories';
    if (selectedIds.length === 1) {
      const category = categories.find(c => c.id === selectedIds[0]);
      return category ? category.name : 'All Categories';
    }
    return `${selectedIds.length} selected`;
  };

  return (
    <div className="modern-filter-panel">
      {/* Date Controls */}
      <div className="date-controls">
        <button 
          type="button" 
          className="nav-btn" 
          onClick={handlePrevMonth}
          title="Previous Month"
        >
          &#8249;
        </button>
        
        <button 
          type="button" 
          className="month-btn"
          title="Current Month Filter"
        >
          {formatMonthButton(currentMonth)}
        </button>
        
        <button 
          type="button" 
          className="nav-btn" 
          onClick={handleNextMonth}
          title="Next Month"
        >
          &#8250;
        </button>

        <div className="form-group date-picker-group">
          <DatePicker
            selected={filters.startDate}
            onChange={handleStartDateChange}
            selectsStart
            startDate={filters.startDate}
            endDate={filters.endDate}
            className="date-input form-control"
            dateFormat="MM/yyyy"
            showMonthYearPicker
            placeholderText="Start Month"
          />
        </div>

        <div className="form-group">
          <DatePicker
            selected={filters.endDate}
            onChange={handleEndDateChange}
            selectsEnd
            startDate={filters.startDate}
            endDate={filters.endDate}
            minDate={filters.startDate}
            className="date-input form-control"
            dateFormat="MM/yyyy"
            showMonthYearPicker
            placeholderText="End Month"
          />
        </div>
      </div>

      {/* Filter Controls */}
      <div className="other-filters">
        <div className="form-group form-group-record-type">
          <select 
            className="form-control form-select"
            value={filters.recordType}
            onChange={(e) => onFilterChange({...filters, recordType: e.target.value})}
            title="Select record type"
          >
            <option value="">All Types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
        </div>
        <div className="form-group multi-select-container">
          <div 
            className="form-control form-select form-select-container"
            onClick={toggleDropdown}
            title="Select categories"
          >
            <span className="selected-text">{getSelectedCategoriesText()}</span>
          </div>
          {isDropdownOpen && (
            <div className="dropdown-panel">
              <div className="dropdown-header">
                <span className="dropdown-title">Select Categories</span>
                <div className="dropdown-actions">
                  <button 
                    type="button"
                    className="icon-btn"
                    title="Select All"
                    onClick={() => onFilterChange({ ...filters, categoryId: categories.map(c => c.id) })}
                  >
                    <FontAwesomeIcon icon={faCheckSquare} />
                  </button>
                  <button 
                    type="button"
                    className="icon-btn"
                    title="Clear All"
                    onClick={clearAllCategories}
                  >
                    <FontAwesomeIcon icon={faEraser} />
                  </button>
                  <button 
                    type="button"
                    className="close-dropdown-btn"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="category-search-container">
                <input
                  type="text"
                  className="form-control search-input category-search-input"
                  placeholder="Search category..."
                  value={categorySearch}
                  onChange={e => setCategorySearch(e.target.value)}
                />
              </div>
              <div className="dropdown-content">
                {categories.filter(c => c.name.toLowerCase().includes(categorySearch.toLowerCase())).length > 0 ? (
                  categories
                    .filter(c => c.name.toLowerCase().includes(categorySearch.toLowerCase()))
                    .map((category) => (
                      <div key={category.id} className="dropdown-option">
                        <span
                          className={`category-dot ${category.type === 'expense' ? 'category-dot-expense' : 'category-dot-income'}`}
                        ></span>
                        <input
                          type="checkbox"
                          className="option-checkbox"
                          checked={filters.categoryId?.includes(category.id) || false}
                          onChange={() => handleCategoryChange(category.id)}
                          title={`Select ${category.name}`}
                        />
                        <span className="option-label">{category.name}</span>
                      </div>
                    ))
                ) : (
                  <div className="no-options">No categories available</div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Currency select dropdown removed as requested */}
        <div className="form-group form-group-min-width-32">
          <button 
            type="button" 
            className="search-btn"
            onClick={onSearch}
          >
            🔍
          </button>
        </div>
        <div className="form-group form-group-min-width-32">
          <button 
            type="button" 
            className="reset-btn"
            onClick={onResetFilters}
          >
            ↻
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonthlySummaryFilters;
